import * as React from 'react'

import type { Field } from 'factorial-form'
import { Form } from 'factorial-form'
import { computed, makeObservable } from 'mobx'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import i18n from 'modules/core/lib/i18n'

import PaymentBill from 'modules/billing/components/PaymentBill'
import PlanModel from 'modules/billing/store/models/Plan'

interface Props {
  plan: PlanModel
  planName: string
  currentSeats: number
  planIntervalReadonly: boolean
  onTogglePricingPeriod?: (field: Field) => void
  onChangePricingPlan?: (planField: Field) => void
}

interface Prices {
  regular: number
  stroked: string
}

class Bill extends React.Component<Props> {
  static defaultProps = {
    planIntervalReadonly: false,
  }

  constructor(props: Props) {
    super(props)
    makeObservable(this, {
      totalPrices: computed,
      hasDiscount: computed,
      prices: computed,
      planForm: computed,
    })
  }

  get totalPrices(): Prices {
    const { plan, currentSeats } = this.props

    const price = this.hasDiscount
      ? plan.discountForNumberOfSeats(currentSeats)
      : plan.priceForNumberOfSeats(currentSeats)

    const stroked = this.hasDiscount
      ? plan.priceWithCurrency(plan.priceForNumberOfSeats(currentSeats))
      : ''

    return {
      regular: price,
      stroked: stroked,
    }
  }

  get hasDiscount(): boolean {
    const { plan } = this.props

    return plan.validDiscount
  }

  get prices(): { regular: number; stroked: string | null } {
    const { plan, currentSeats } = this.props

    const price = this.hasDiscount
      ? plan.discountForNumberOfSeats(currentSeats)
      : plan.priceForNumberOfSeats(currentSeats)

    const perMonth = plan.priceWithCurrency(plan.priceForNumberOfSeats(1))

    const stroked = this.hasDiscount ? i18n.t('billing.bill.per_month', { perMonth }) : null

    return { regular: price, stroked }
  }

  get additionalEmployeePrices(): Record<string, any> {
    const { plan } = this.props

    const price = this.hasDiscount
      ? plan.discountForNumberOfSeats(1)
      : plan.priceForNumberOfSeats(1)

    const perMonth = plan.priceWithCurrency(plan.priceForNumberOfSeats(1))

    const stroked = this.hasDiscount
      ? i18n.t('billing.bill.per_month', {
          perMonth,
        })
      : null

    return {
      regular: price,
      stroked: stroked,
    }
  }

  get planForm(): Form {
    const { plan } = this.props

    return new Form(
      {
        interval: plan.attributes.interval,
        family: plan.attributes.family,
      },
      {
        interval: 'string',
        family: 'string',
      }
    )
  }

  renderPlanSelector() {
    const { planName, onChangePricingPlan } = this.props

    if (!onChangePricingPlan) return planName

    return (
      <Select
        onChange={onChangePricingPlan}
        field={this.planForm.get('family')}
        options={[
          {
            label: i18n.t('pricing_plans.plan_names.essentials'),
            value: PlanModel.AVAILABLE_FAMILIES.essentials,
          },
          {
            label: i18n.t('pricing_plans.plan_names.premium'),
            value: PlanModel.AVAILABLE_FAMILIES.premium,
          },
        ]}
      />
    )
  }

  renderIntervalSelector() {
    const { onTogglePricingPeriod, planIntervalReadonly } = this.props
    const field = this.planForm.get('interval')
    const yearlyLabel = i18n.t('pricing_plans.period_switcher.yearly')
    const monthlyLabel = i18n.t('pricing_plans.period_switcher.monthly')

    if (!onTogglePricingPeriod || planIntervalReadonly) {
      return field.value === PlanModel.intervals.year ? yearlyLabel : monthlyLabel
    }

    return (
      <Select
        field={field}
        onChange={onTogglePricingPeriod}
        options={[
          {
            label: yearlyLabel,
            value: 'year',
          },
          {
            label: monthlyLabel,
            value: 'month',
          },
        ]}
      />
    )
  }

  render() {
    const { plan, planName, currentSeats } = this.props

    const additionalEmployeeText = this.hasDiscount
      ? i18n.t('billing.bill.up_to_employees', {
          numEmployees: 20,
        })
      : ''

    const plusTaxes = i18n.t('billing.bill.plus_taxes')

    const redeemDate = plan.formatedDiscountDurationDate

    const infoDiscount = this.hasDiscount
      ? i18n.t('billing.bill.discount_until', {
          redeemDate,
        })
      : ''

    return (
      <PaymentBill
        title={i18n.t('billing.payment.plan_title', {
          plan: planName,
        })}
      >
        <PaymentBill.Row>
          <PaymentBill.Text label={i18n.t('billing.bill.plan')} />
          <PaymentBill.Value>{this.renderPlanSelector()}</PaymentBill.Value>
        </PaymentBill.Row>
        <PaymentBill.Row>
          <PaymentBill.Text label={i18n.t('billing.bill.period')} />
          <PaymentBill.Value>
            <PaymentBill.Value>{this.renderIntervalSelector()}</PaymentBill.Value>
          </PaymentBill.Value>
        </PaymentBill.Row>
        <PaymentBill.Row>
          <PaymentBill.Text
            label={i18n.t('billing.bill.additional_employee_price')}
            smallLabel={additionalEmployeeText}
          />
          <PaymentBill.Value
            amount={i18n.t('billing.bill.per_month', {
              perMonth: plan.priceWithCurrency(this.additionalEmployeePrices.regular),
            })}
            strokedAmount={this.additionalEmployeePrices.stroked}
          />
        </PaymentBill.Row>
        <PaymentBill.Row>
          <PaymentBill.Text label={i18n.t('billing.bill.employees_created')} />
          <PaymentBill.Value amount={currentSeats.toString()} />
        </PaymentBill.Row>
        <PaymentBill.Row>
          <PaymentBill.Text label={i18n.t('billing.bill.total')} smallLabel={infoDiscount} />
          <PaymentBill.Value
            amount={`${plan.priceWithCurrency(this.totalPrices.regular)} ${plusTaxes}`}
            strokedAmount={this.totalPrices.stroked}
          />
        </PaymentBill.Row>
      </PaymentBill>
    )
  }
}

export default observer(Bill)
